import {
  get,
  post,
  put,
  deleteFile,
  postDigitalContent,
  postHEICContent,
  getWithoutBearer,
} from "./index";

const api = {
  // COMMON
  getVersionInfo: () => get("/api/versionInfo"),
  getClientSettings: clientId => get(`/api/3/clientsettings/${clientId}/public-settings`),
  getNavigationMenu: () => get("/api/3/menu/employee"),
  getNewNavMenus: () => get("/api/4/menu/employee"),
  getCustomCss: clientName => post("/api/4/menu/getCustomCss", { companyId: clientName }),

  getContactInfo: data => post("/api/4/help/contactInfo", data),

  login: data => post("/api/3/user/login", data),
  loginOauth: data => post("/connect/account/login", data),
  logout: () => post("/api/3/user/logout", null, { noIntercept: true }),
  logoutIdp: () => post("/connect/account/logout", null, { noIntercept: true }),
  upgradeUserSession: data => post("/api/3/user/upgradesession", data),
  updateUserRole: userRole => post(`/api/4/user/updatesessionrole?userRole=${userRole}`),
  getAuthAssertion: (spID = "", nonce = "") =>
    getWithoutBearer(`/api/3/saml/idp/auth?serviceProviderId=${spID}&nonce=${nonce}`),
  getShortLivedToken: () => get("/api/3/user/slToken"),
  authorizeUserSession: () => post("/connect/account/authenticate"),
  requestPasswordReset: data => post("/api/3/user/password/reset", data),
  resetPassword: data => post("/api/3/user/password/reset/token", data),
  changePassword: data => post("/api/3/user/password/change", data),
  passwordPolicy: data => get("/api/3/user/password/policy", data),
  getNeedHelp: () => get("/api/3/help"),
  getNeedHelpArticle: data => get("/api/3/content/".concat(data.articleId)),
  getUserProfile: () => get("/api/3/employee/profile"),
  updateUserProfile: data =>
    post(`/api/3/employee/profile/employee/${data.employeeKey}/update`, data),
  // Dependant
  getDependantFields: () => get("/api/3/employee/dependant/fields"),
  updateDependant: data =>
    post(
      `/api/3/employee/profile/employee/${data.employeeKey}/dependant/${data.dependantKey}/update`,
      data
    ),
  addDependant: data =>
    post(`/api/3/employee/profile/employee/${data.employeeKey}/dependant/add?`, data),

  // Benefits
  getBenefitsDocuments: () => get("/api/3/benefit/documents"),
  getDocuments: type => get(`/api/4/benefit/documents/web/${type}`),
  getTrsBenefitPeriodList: () => get("/api/3/benefit/trs/benefitperiodlist"),
  getTrsDetail: benefitPeriod => get(`/api/3/benefit/trs?benefitPeriod=${benefitPeriod}`),
  /* Test data for getBenefitsDocument() */
  //  getBenefitsSummary: () => api.dummyApiCall({type: "api.3.enrolment.statement"}),
  getBenefitsSummary: data => get("/api/3/enrolment/statement", data),
  getBenefitStatement: data => get("/api/4/benefit/statement", data),
  getBenefitSettings: data => get("/api/4/benefit/settings", data),
  getSpendingAccounts: data => get("/api/3/account/soa", data),
  getNewSpendingAccounts: data => get("/api/4/account/soa", data),
  getAccountEnrolmentSummary: data => get("/api/3/account/enrolment/summary", data),
  getBenefitsSurvey: () => get("/api/3/enrolment/employeeSurvey"),
  saveBenefitsSurvey: (surveyId, data) =>
    post(`/api/3/enrolment/employeeSurvey/save?surveyId=${surveyId}`, data),
  postSurveyDocument: file =>
    postDigitalContent("/api/3/digitalcontent/upload/Survey/SurveyDocument", file),

  getEnrolmentSettings: () => get("/api/4/enrolment/settings"),
  getEnrolmentBenefits: benefitType => get(`/api/4/enrolment`),

  saveEnrolmentSelection: data => post(`/api/4/enrolment`, data),
  validateEnrolmentSelection: data => post(`/api/4/enrolment/validate`, data),

  getEnrolment: () => get("/api/3/enrolment"),
  // getEnrolment: () => api.dummyApiCall({ type: "enrolment-per-member" }),
  cacheEnrolment: () => get("/api/3/enrolment/cacheEnrolment"),
  /* Mock data:
  getEnrolment: () => api.dummyApiCall({type:"enrolmentWithValidProductSummaryFiles"}),
  */
  saveEnrolment: data => post("/api/3/enrolment/save", data),
  getCheckoutSummary: (isCheckoutPage = false) =>
    get("/api/3/enrolment/checkout/summary", {
      isCheckoutPage,
    }), //api.dummyApiCall({type:"checkoutItemExpired"}),
  getEnrolmentCheckoutPreview: () => get("/api/4/enrolment/summary"),
  // getEnrolmentCheckoutPreview: () => {
  //   return new Promise((resolve, reject) => {
  //     resolve(enrolmentPreview);
  //   });
  // },
  removeExpiredItemsFromShoppingCart: () => post("/api/3/enrolment/checkout/delete/expiredItems"),
  proceedCheckout: agreed => post(`/api/3/enrolment/checkout/proceed?hasAgreed=${agreed}`),
  checkout: (hasAgreed, hasAgreedSalaryDeduction) => {
    const url = `/api/3/enrolment/checkout/proceed?hasAgreed=${hasAgreed}`;
    return post(
      hasAgreedSalaryDeduction ? url + `&hasAgreedSalaryDeduction=${hasAgreedSalaryDeduction}` : url
    );
  },
  checkoutEnrolment: (hasAgreedConsent, hasAgreedSalaryDeduction) => {
    const url = `/api/4/enrolment/checkout?hasAgreedConsent=${hasAgreedConsent}`;
    return post(
      hasAgreedSalaryDeduction ? url + `&hasAgreedSalaryDeduction=${hasAgreedSalaryDeduction}` : url
    );
  },
  getUserProxyToken: proxyToken => get("/api/3/user/proxy", { token: proxyToken }),
  //other benefits
  getOtherBenefits: data => get("/api/4/benefit/others", data),
  // getOtherBenefits: () => {
  //   return new Promise((resolve, reject) => {
  //     resolve(sample2);
  //   });
  // },
  postOtherBenefits: data => post("/api/4/benefit/save", data),

  // Library
  getLibraryCatalogItems: data => get("/api/3/library/doc", data),
  getLibraryItem: itemKey => get(`/api/3/library/doc/${itemKey}`),

  // DASHBOARD
  getDashboard: () => get("/api/3/employee/dashboard"),
  getDashboardPP: () => get("/api/4/employee/dashboard"),
  getMessageBoard: () => get("/api/4/employee/messageboard"),
  getInsuranceAds: () => get("/api/4/employee/insuranceAds"),
  getDashboardActions: () => get("/api/4/employee/actions"),
  // getNotifications: () => get("/api/3/employee/dashboard"),
  ackNotification: data => post("/api/3/employee/dashboard/acknowledge", data),
  postCharitySurvey: data => post("/api/3/charity/charityanswer", data),
  getEmployeeNotifications: (count, page, pageSize) =>
    get(`api/3/pushnotification/notifications?count=${count}&page=${page}&pageSize=${pageSize}`),
  setNotificationsRead: id => put(`/api/3/pushnotification/notifications/${id}/read`),
  // Wellness
  getBasicScreeningResults: () => get("/api/3/wellness/basicScreenResult"),
  getBasicScreeningResultsToPrint: () => get("/api/2/wellness/basicScreenPrint"),
  getAdvancedScreeningResults: () => get("/api/2/wellness/advancedScreenResult"),
  getAdvancedScreeningResultDetails: marker =>
    get(`/api/2/wellness/advancedScreenResult/detail/${marker}`),

  // CLAIMS
  getPendingClaims: ({ benefitPeriod, selectedDelegator, getDonateCategory }) =>
    selectedDelegator
      ? get("/api/3/claim/list/pending", {
          benefitPeriod: `${benefitPeriod || "current"}`,
          selectedDelegator,
          getDonateCategory,
        })
      : get("/api/3/claim/list/pending", {
          benefitPeriod: `${benefitPeriod || "current"}`,
          getDonateCategory,
        }),
  getClaimsHistory: ({ benefitPeriod, selectedDelegator, getDonateCategory }) =>
    selectedDelegator
      ? get(`/api/3/claim/list?benefitPeriod=${benefitPeriod || ""}`, {
          selectedDelegator,
          getDonateCategory,
        })
      : get(`/api/3/claim/list?benefitPeriod=${benefitPeriod || ""}`, { getDonateCategory }),
  getBenefitPlans: () => get("/api/3/benefit/plans"),
  getClaimItems: data => get("/api/3/claim/items", data),
  getClaimCategories: (planId, selectedDelegator, isServiceProvider = false) => {
    let url = "/api/4/claims/items?";
    if (planId) {
      url += `planId=${planId}&`;
    }
    if (selectedDelegator) {
      url += `selectedDelegator=${selectedDelegator}`;
    }
    if (isServiceProvider) {
      url += `isServiceProviderClaimItem=${isServiceProvider}`;
    }
    return get(url.charAt(url.length - 1) === "&" ? url.slice(0, -1) : url);
  },
  getClaimAccounts: (planId, claimItemId, selectedDelegator) => {
    let url = " /api/4/claims/accounts?";
    if (planId) {
      url = url + `planId=${planId}&`;
    }
    if (claimItemId) {
      url = url + `claimItemId=${claimItemId}&`;
    }
    if (selectedDelegator) {
      url += `selectedDelegator=${selectedDelegator}`;
    }
    return get(url.charAt(url.length - 1) === "&" ? url.slice(0, -1) : url);
  },
  getClaimItemFields: data => get("/api/3/claim/fields", data),
  getClaimItem: data => get("/api/4/claims/item/info", data),
  checkClaimReachLimit: data => get("/api/4/claims/reachlimit", data),
  getCurrencyExchangeRates: data => get("/api/4/claims/currency/conversions", data),

  convertHeic: file => postHEICContent("/api/4/claims/receipt/convertHeicToJpeg", file),

  uploadClaimReceipt: (file, selectedDelegator) =>
    postDigitalContent(
      selectedDelegator
        ? `/api/4/claims/receipt/upload?selectedDelegator=${selectedDelegator}`
        : "/api/4/claims/receipt/upload",
      file
    ),

  uploadClaimReferral: (file, selectedDelegator) =>
    postDigitalContent(
      selectedDelegator
        ? `/api/4/claims/referral/upload?selectedDelegator=${selectedDelegator}`
        : "/api/4/claims/referral/upload",
      file
    ),
  uploadClaimDocument: (file, selectedDelegator) =>
    postDigitalContent(
      selectedDelegator
        ? `/api/4/claims/document/upload?selectedDelegator=${selectedDelegator}`
        : "/api/4/claims/document/upload",
      file
    ),

  removeClaimReceipt: (objectId, selectedDelegator) =>
    deleteFile(
      selectedDelegator
        ? `/api/4/claims/receipt/upload?objectId=${objectId}&selectedDelegator=${selectedDelegator}`
        : `/api/4/claims/receipt/upload?objectId=${objectId}`
    ),

  removeClaimReferral: (objectId, selectedDelegator) =>
    deleteFile(
      selectedDelegator
        ? `/api/4/claims/referral/upload?objectId=${objectId}&selectedDelegator=${selectedDelegator}`
        : `/api/4/claims/referral/upload?objectId=${objectId}`
    ),

  removeClaimDocument: (objectId, selectedDelegator) =>
    deleteFile(
      selectedDelegator
        ? `/api/4/claims/document/upload?objectId=${objectId}&selectedDelegator=${selectedDelegator}`
        : `/api/4/claims/document/upload?objectId=${objectId}`
    ),

  getReceiptOCR: (objectId, selectedDelegator) =>
    get(
      selectedDelegator
        ? `/api/4/claims/receipts/${objectId}/ocr?selectedDelegator=${selectedDelegator}`
        : `/api/4/claims/receipts/${objectId}/ocr`
    ),

  saveClaimDraft: (data, selectedDelegator, limitAccepted = false, userAccepted = false) => {
    let url = `/api/4/claims/save?limitAccepted=${limitAccepted}&userAccepted=${userAccepted}&`;
    if (selectedDelegator) {
      url += `selectedDelegator=${selectedDelegator}`;
    }

    return post(url.charAt(url.length - 1) === "&" ? url.slice(0, -1) : url, data);
  },

  submitClaim: (claimId, isConsent = false, selectedDelegator) =>
    post(
      selectedDelegator
        ? `/api/4/claims/${claimId}/submit?consentGranted=${isConsent}&selectedDelegator=${selectedDelegator}`
        : `/api/4/claims/${claimId}/submit?consentGranted=${isConsent}`
    ),

  getClaimDocuments: (claimId, selectedDelegator) =>
    get(
      selectedDelegator
        ? `/api/4/claims/documents/${claimId}?selectedDelegator=${selectedDelegator}`
        : `/api/4/claims/documents/${claimId}`
    ),
  saveClaimDocuments: (claimId, data, selectedDelegator) =>
    post(
      selectedDelegator
        ? `/api/4/claims/documents/${claimId}?selectedDelegator=${selectedDelegator}`
        : `/api/4/claims/documents/${claimId}`,
      data
    ),
  getClaimDetail: (claimId, selectedDelegator) =>
    get(
      selectedDelegator
        ? `/api/4/claims/${claimId}?selectedDelegator=${selectedDelegator}`
        : `/api/4/claims/${claimId}`
    ),
  getClaimDashboard: (planId, selectedDelegator) => {
    let url = "/api/4/claims/dashboard?";
    if (planId) {
      url += `planId=${planId}&`;
    }
    if (selectedDelegator) {
      url += `selectedDelegator=${selectedDelegator}`;
    }
    return get(url.charAt(url.length - 1) === "&" ? url.slice(0, -1) : url);
  },

  getAllClinics: type =>
    type
      ? get(`/api/4/serviceprovider/list?providerType=${type}`)
      : get(`/api/4/serviceprovider/list`),
  getSearchClinics: data => {
    const queryParams = Object.entries(data)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join("&");
    return get(`/api/4/serviceprovider/search?${queryParams}`);
  },

  getClaimItemAccounts: data => get("/api/3/claim/item/accounts", data),
  getDisplayClaimItemFields: () => get("/api/3/claim/newClaim/AccountFields"),
  getClaimLimitMessages: data => get("/api/3/claim/limitMessages", data),
  getBalanceDetails: (planYear, accountType) =>
    get(`/api/3/account/balanceDetails?planId=${planYear}&acctType=${accountType}`),
  postClaim: (data, userAccepted = false, limitAccepted = false) =>
    post(`/api/3/claim/submit?userAccepted=${userAccepted}&limitAccepted=${limitAccepted}`, data),
  postClaimReceipt: (file, selectedDelegator, enhance) =>
    postDigitalContent(
      selectedDelegator
        ? `/api/3/digitalcontent/upload/Claim/Receipt?selectedDelegator=${selectedDelegator}`
        : "/api/3/digitalcontent/upload/Claim/Receipt",
      file,
      enhance
    ),
  postClaimReferral: (file, selectedDelegator) =>
    selectedDelegator
      ? postDigitalContent(
          `/api/3/digitalcontent/upload/Claim/Referral?selectedDelegator=${selectedDelegator}`,
          file
        )
      : postDigitalContent("/api/3/digitalcontent/upload/Claim/Referral", file),
  getClaim: ({ claimId, selectedDelegator, getDonateCategory }) =>
    selectedDelegator
      ? get(`/api/3/claim/${claimId}`, { selectedDelegator, getDonateCategory })
      : get(`/api/3/claim/${claimId}`, { getDonateCategory }),
  deleteClaim: ({ claimId, selectedDelegator }) =>
    selectedDelegator
      ? post(`/api/3/claim/delete/${claimId}?selectedDelegator=${selectedDelegator}`)
      : post(`/api/3/claim/delete/${claimId}`),
  deleteClaimReceipt: (claimObjectId, objectId, selectedDelegator) =>
    selectedDelegator
      ? post(
          `/api/3/claim/${claimObjectId}/deleteReceipt/${objectId}?selectedDelegator=${selectedDelegator}`
        )
      : post(`/api/3/claim/${claimObjectId}/deleteReceipt/${objectId}`),

  getEmployeesByDelegation: () => get("/api/3/delegation/getListOfEmployeeByDelegation"),
  postExpenseClaims: ({decision, objectId, delegatorKey, data}) => post(`/api/4/claims/expense/${decision}/${objectId}?selectedDelegator=${delegatorKey}`, data),
  getClaimsNeedHelp: () => get("/api/4/claims/needhelp"),
  postClaimsNeedHelp: data => post("/api/4/claims/claimtype", data),
  getECardsList: () => get("/api/3/serviceprovider/getecardlist"),

  // Wellness
  getWellnessLaunchpad: () => get("/api/2/wellness/dashboard"),
  getMentalWellness: () => get("/api/4/wellness/servicescontent"),
  getHraQuestionnaire: () => get("/api/2/wellness/hraInput"),
  postHraQuestionnaireAnswers: (isComplete, sectionTag, data) =>
    post(`/api/2/wellness/hrainput?isComplete=${isComplete}&sectionTag=${sectionTag}`, data),
  getShopActivities: (page, keyword, includeSignUps) =>
    get(`/api/3/activities?page=${page}&includeSignUps=${includeSignUps}&searchKeyword=${keyword}`),
  getSignedUpActivities: () => get("/api/3/activities/signups"),
  getShopActivityDetails: activity => get(`/api/3/activities/${activity}?includeSignUps=true`),
  resendActivityDetails: data => post(`/api/3/activities/signUps/resend/${data.signUpCode}`, data),
  cancelActivitySignup: data => post(`/api/3/activities/signUps/cancel/${data.signUpCode}`),
  signupForActivity: activity => post(`/api/3/activities/signUps/add/${activity}`),
  getHraResult: () => get("/api/2/wellness/hraResult"),
  getHraResultDetails: riskDomain => get(`/api/2/wellness/hraResult/${riskDomain}`),
  getFaceAgeingPhoto: imgUrl => get(imgUrl),
  setEthnicity: ethnicity => post("/api/2/wellness/ethnicity", ethnicity),
  uploadFaceAgeingImage: data => post("/api/2/wellness/uploadImage", data),
  postAgeFace: data => post("/api/2/wellness/age", data),
  getFaceAgeingImage: data => get("/api/2/wellness/image", data),
  postReAge: data => post("/api/2/wellness/reage", data),
  getAgingStatus: () => get("/api/2/wellness/agingStatus"),
  getWellnessPriorities: () => get("/api/3/wellness/riskfactorpriority"),
  postWellnessPriorities: data => post("/api/3/wellness/riskfactorpriority", data),

  // CONSENT
  getConsentMessage: () => get("/api/3/user/consent"),
  postGrantConsent: data => post("/api/3/user/consent", data),

  // HDF
  getHdf: (hdfId, planYearId, enrolmentKey) => {
    let hdfUrl = `/api/3/enrolment/hdf?HDFormID=${encodeURIComponent(hdfId)}`;

    if (planYearId) {
      hdfUrl += `&defaultPlanId=${planYearId}`;
    }
    if (enrolmentKey) {
      hdfUrl += `&enrolmentKey=${enrolmentKey}`;
    }
    return get(hdfUrl);
  },
  getHdfAndMembersWithUW: (hdfId, planYearId, enrolmentKey) => {
    // eslint-disable-next-line no-secrets/no-secrets
    let hdfUWUrl = `/api/3/enrolment/hdfAndMembersWithUW?HDFormID=${encodeURIComponent(hdfId)}`;
    if (planYearId) {
      hdfUWUrl += `&defaultPlanId=${planYearId}`;
    }
    if (enrolmentKey) {
      hdfUWUrl += `&enrolmentKey=${enrolmentKey}`;
    }
    return get(hdfUWUrl);
  },
  saveHdf: data => post("/api/3/enrolment/hdf/save", data),
  confirmHdf: data => post("/api/3/enrolment/hdf/confirm", data),

  //Localization
  getLanguages: data => get(`/api/3/globalization/${data.client}/languages`),
  getLanguageResouce: data =>
    get(`/api/3/globalization/resources/${data.client}/${data.languageId}`),

  getClientLanguages: clientName => get(`/api/3/globalization/${clientName}/languages`),
  getClientLocaleData: (clientName, langCode) =>
    get(`/api/3/globalization/resources/${clientName}/${langCode}`),

  // Delegation
  getDelegationList: () => get("/api/3/delegation"),
  getEmployeeDelegation: data => get("/api/3/delegation/searchEmployee", data),
  postCheckEmployeeDelegation: data => post("/api/3/delegation/check", data),
  postAddEmployeeDelegation: data => post("/api/3/delegation/create", data),
  postEditEmployeeDelegation: ({ delegateKey, ...restData }) =>
    post(`/api/3/delegation/update?delegateKey=${delegateKey}`, restData),
  postRevokeDelegation: ({ delegateKey }) =>
    post(`/api/3/delegation/revoke?delegateKey=${delegateKey}`),

  // In - App Message
  getUnreadMessageCount: () => get("/api/3/messaging/unreadMessagesCount"),
  getRootMessages: () => get("/api/3/messaging/rootMessages"),
  getChildrenMessages: pkMessage =>
    get(`/api/3/messaging/childrenMessages?rootMessageKey=${pkMessage}`),
  searchMessages: searchTerm => get(`/api/3/messaging/searchMessages?searchTerm=${searchTerm}`),
  readMessage: messageKey => post(`/api/3/messaging/readMessage?messageKey=${messageKey}`),
  uploadMessageAttachment: file =>
    postDigitalContent("/api/3/messaging/upload/message/message", file),
  postMessage: data => post("/api/3/messaging/message", data),
  getGlobalMessageBoard: () => get("/api/3/globalization/globalmessageboard"),

  //twoFA
  sendOTP: (twoFaMode, isResend) => {
    const data = { twoFaMode };
    return post(`/api/4/2fa/send?${isResend ? "isResend=true" : "isResend=false"}`, data);
  },

  sendOTPForm: data => {
    if (data.requestToken) {
      return post(`/api/4/2fa/send?token=${data.requestToken}`, data);
    } else {
      return post(`/api/4/2fa/send?`, data);
    }
  },
  verifyOTP: data => post("/api/3/2fa/verify", data),
  sendVerificationEmail: () => post("/api/3/2fa/sendVerificationEmail"),
  // eslint-disable-next-line no-secrets/no-secrets
  get2FACountryCodes: clientId => post("/api/4/2fa/getCountryList", { clientId }),
  get2FASettings: (clientId, token) => {
    if (clientId && token) {
      return post(`/api/4/2fa/get2FAMobile?token=${token}`, { clientId });
    } else {
      post("/api/4/2fa/get2FAMobile");
    }
  },

  // marketplace

  getMarketplaceProducts: ({ page = 0, pageSize = 5, searchKey } = {}) => {
    const queryParams = searchKey
      ? `page=${page}&pagesize=${pageSize}&searchkey=${encodeURIComponent(searchKey)}`
      : `page=${page}&pagesize=${pageSize}`;
    return get(`/api/3/shop/products?${queryParams}`);
  },

  getProviderProducts: (providerId, params) => {
    let queryParams = "";
    if (params && params.page && params.pageSize) {
      queryParams += `page=${params.page}&pagesize=${params.pageSize}`;
    } else {
      queryParams += `page=0&pagesize=10`;
    }
    return get(`/api/3/shop/provider/${providerId}/products?${queryParams}`);
  },

  getMarketCategories: () => get("/api/3/shop/categories"),
  getMarketplaceProductDetails: productId => get(`/api/3/shop/products/${productId}`),
  getMarketplaceProviderDetailsProducts: providerId =>
    get(`/api/3/shop/provider/${providerId}/products`),
  getMarketplaceProviderDetails: providerId => get(`/api/3/shop/providers/${providerId}/detail`),
  sendProductOfferByEmail: productId => post(`/api/3/shop/products/${productId}/sendOffer`),
  getMarketplaceCategoryProducts: (categoryId, params) => {
    let queryParams = "";

    if (params && params.page && params.pageSize) {
      queryParams += `page=${params.page}&pagesize=${params.pageSize}`;
    } else {
      queryParams += `page=0&pagesize=10`;
    }
    if (params && params.searchKey) {
      queryParams += `searchKeyword=${params.searchKey}`;
    }
    return get(`/api/3/shop/categories/${categoryId}/products?${queryParams}`);
  },
  getNewsletters: (planId, params) => {
    // Initialize empty array to store query parts
    const queryParts = [];

    // Add planId (calendarYear) if it exists
    if (planId) {
      queryParts.push(`calendarYear=${planId}`);
    }

    // Add pagination params if they exist
    if (params && params.page && params.pageSize) {
      queryParts.push(`page=${params.page}`);
      queryParts.push(`pagesize=${params.pageSize}`);
    }

    // Construct final URL
    const baseUrl = "/api/4/employee/newsletters";
    const queryString = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";

    return get(`${baseUrl}${queryString}`);
  },
  getNewsletterDetail: id => get(`/api/4/employee/newsletters/${id}`),
  endSelectEmployee: () => {
    return post("api/3/hradmin/endSelectEmployee");
  },
  getHDFConfiguration: formId => {
    return get(`/api/4/enrolment/gen-hdf?hdFormId=${formId}`)
  },
  saveGeneralHDF: (data, isLastPage) => {
    return post(`/api/4/enrolment/gen-hdf/${isLastPage? "confirm": "save"}/${data.formId}`, data);
  },
};
export default api;



// WEBPACK FOOTER //
// ./api/flexapi.js